import SimpleTable, { ISimpleTableRow } from "@components/simpleTable";
import { PrEldpEntity, PrEldpItemEntity } from "@odata/GeneratedEntityTypes";
import { StyledItemsSummary } from "@pages/documents/extensions/itemsSummary/ItemsSummary.styles";
import { ItemsSummaryPath } from "@pages/documents/extensions/itemsSummary/ItemsSummary.utils";
import { getEldpCurrencyPath, TEldpFormStorage } from "@pages/payroll/Eldp/Eldp.utils";
import { getPayrollCurrencyFormatter } from "@pages/payroll/payroll.utils";
import { memoizeOne } from "@utils/memoizeOne";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { DASH_CHARACTER } from "../../../constants";
import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { RowType, TextAlign } from "../../../enums";
import TestIds from "../../../testIds";


interface IProps extends WithTranslation {
    storage: TEldpFormStorage;
}

const columns = [PrEldpItemEntity.CalculationBasis, PrEldpItemEntity.DaysExcluded, PrEldpItemEntity.DaysDeducted] as const;

class EldpItemsSummary extends React.Component<IProps> {
    static contextType = AppContext;

    constructor(props: IProps) {
        super(props);

        // register ItemsSummary on storage, so that it can be refreshed as any other field
        props.storage.addRef(this, props.storage.data.bindingContext.navigate(ItemsSummaryPath));
    }

    componentWillUnmount() {
        this.props.storage.removeRef(this, this.props.storage.data.bindingContext.navigate(ItemsSummaryPath));
    }

    columns = memoizeOne(() => columns.map(id => ({
        id, label: this.props.t(`Eldp:ItemsSummary.${id}`),
        textAlign: TextAlign.Right
    })));

    render() {
        const { storage } = this.props;
        const { Items } = storage.data.entity;
        const itemsBc = storage.data.bindingContext.navigate(PrEldpEntity.Items);
        const rows: ISimpleTableRow[] = [];

        const _formatCurrency = (val: number): string =>
                val ? getPayrollCurrencyFormatter(getEldpCurrencyPath(), true)(val, { storage }) : DASH_CHARACTER;

        const sum = {
            [PrEldpItemEntity.CalculationBasis]: 0,
            [PrEldpItemEntity.DaysExcluded]: 0,
            [PrEldpItemEntity.DaysDeducted]: 0
        };

        for (const item of (Items ?? [])) {
            const itemBc = itemsBc.addKey(item);

            if (columns.some(col => storage.getError(itemBc.navigate(col)))) {
                continue;
            }

            columns.forEach(col => {
                sum[col] += item[col] ?? 0;
            });

            rows.push({
                id: itemBc.getKey(),
                type: RowType.Value,
                values: {
                    [PrEldpItemEntity.CalculationBasis]: _formatCurrency(item.CalculationBasis),
                    [PrEldpItemEntity.DaysExcluded]: item.DaysExcluded ?? DASH_CHARACTER,
                    [PrEldpItemEntity.DaysDeducted]: item.DaysDeducted ?? DASH_CHARACTER
                }
            });
        }

        const Summary = {
            id: "sum",
            type: RowType.Aggregation,
            values: {
                [PrEldpItemEntity.CalculationBasis]: _formatCurrency(sum[PrEldpItemEntity.CalculationBasis]) ?? DASH_CHARACTER,
                [PrEldpItemEntity.DaysExcluded]: sum[PrEldpItemEntity.DaysExcluded] ? sum[PrEldpItemEntity.DaysExcluded] : DASH_CHARACTER,
                [PrEldpItemEntity.DaysDeducted]: sum[PrEldpItemEntity.DaysDeducted] ? sum[PrEldpItemEntity.DaysDeducted] : DASH_CHARACTER
            }
        };

        rows.push(Summary);

        return (
                <StyledItemsSummary data-testid={TestIds.ItemsSummaryTable}>
                    <SimpleTable
                            columns={this.columns()}
                            rows={rows}
                            disableFocus
                    />
                </StyledItemsSummary>
        );
    }

}

export default withTranslation(["Eldp"])(EldpItemsSummary);